import React from 'react';
import type { SxProps, Theme } from '@mui/material';
import { useBrandRegistry } from '../../hooks/useBrandRegistry';
import { ChipInput } from './ChipInput';

export interface BrandMultiSelectProps {
  /** Brand names. */
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Message on a chip that is not a registry brand; it is still kept. */
  notListedMessage?: string;
  sx?: SxProps<Theme>;
  'aria-label': string;
}

/**
 * Several brands as chips, picked from the brand registry (the list `BrandSelect`
 * offers); a typed name the registry lacks is kept and flagged with `notListedMessage`.
 */
export const BrandMultiSelect: React.FC<BrandMultiSelectProps> = ({
  value,
  onChange,
  placeholder,
  disabled,
  notListedMessage,
  sx,
  'aria-label': ariaLabel,
}) => {
  const { data } = useBrandRegistry();
  const names = React.useMemo(
    () =>
      (data || [])
        .map((brand) => brand.name)
        .filter((name): name is string => Boolean(name))
        .sort((a, b) => a.localeCompare(b)),
    [data],
  );
  const known = React.useMemo(() => new Set(names.map((name) => name.toLowerCase())), [names]);
  const validate = React.useCallback(
    (brand: string) => (notListedMessage && names.length > 0 && !known.has(brand.toLowerCase()) ? notListedMessage : null),
    [known, names.length, notListedMessage],
  );

  return (
    <ChipInput
      value={value}
      onChange={onChange}
      suggestions={names}
      validate={validate}
      placeholder={placeholder}
      disabled={disabled}
      commaSeparates={false}
      sx={sx}
      aria-label={ariaLabel}
    />
  );
};
